import {
  type WorkflowRunsRoutePayload,
  WorkflowRunsRoutePayloadSchema,
} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import {QueryRouteQueryType} from '@github-ui/react-core/future/data-router-types'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {workflowRouteParam} from '../../workflow-path'

export interface WorkflowRunsQueryOptions {
  owner: string
  repo: string
  workflow_file_name?: string
  query: string
  page: number
  lab?: boolean
}

interface WorkflowRunsRouteParams {
  owner?: string
  repo?: string
  workflowFileName?: string
  '*'?: string
  workflow_file_name?: string
}

interface WorkflowRunsQueryDepsArgs {
  params: WorkflowRunsRouteParams
  searchParams: URLSearchParams
}

export function resolveWorkflowRunsQueryOptions({
  params,
  searchParams,
}: WorkflowRunsQueryDepsArgs): WorkflowRunsQueryOptions {
  const pageParam = Number(searchParams.get('page'))
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1
  const workflowFileName = workflowRouteParam(params)

  return {
    owner: params.owner ?? '',
    repo: params.repo ?? '',
    workflow_file_name: params.workflow_file_name ?? (workflowFileName || undefined),
    query: searchParams.get('query')?.trim() ?? '',
    page,
    lab: searchParams.get('lab') === 'true',
  }
}

export function removeWorkflowRunsPageSearchParam(searchParams: URLSearchParams) {
  const next = removeReactToggleSearchParam(searchParams)
  next.delete('page')
  return next
}

export function removeWorkflowRunsQueryAndPageSearchParams(searchParams: URLSearchParams) {
  const next = removeWorkflowRunsPageSearchParam(searchParams)
  next.delete('query')
  // `lab` only makes sense scoped to the currently selected workflow; callers that build
  // links to a different workflow (or to "All workflows") must not carry it over as stale
  // state. `workflowRouteHref` re-adds it when the target workflow is itself a lab workflow.
  next.delete('lab')
  return next
}

export function removeReactToggleSearchParam(searchParams: URLSearchParams) {
  const next = new URLSearchParams(searchParams)
  next.delete('react')
  return next
}

export function workflowRunsQueryPath({owner, repo}: Pick<WorkflowRunsQueryOptions, 'owner' | 'repo'>) {
  return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/actions/workflow-runs`
}

export function workflowRunsQueryDeps({params, searchParams}: WorkflowRunsQueryDepsArgs) {
  const options = resolveWorkflowRunsQueryOptions({params, searchParams})

  return {
    pathname: workflowRunsQueryPath(options),
    searchParams: {
      workflow_file_name: options.workflow_file_name ?? null,
      query: options.query || null,
      page: options.page > 1 ? String(options.page) : null,
      lab: options.lab ? 'true' : null,
    },
  }
}

// Deferred navigation lets filter and page changes render with a local loading state.
// `mainQuery` extracts `payload[routeId]` and decodes the generated proto on both
// ingest paths: embedded first paint and soft-navigation fetches.
const workflowRunsMainQueryBase = mainQuery<WorkflowRunsRoutePayload>({
  queryDeps: workflowRunsQueryDeps,
  schema: WorkflowRunsRoutePayloadSchema,
})

export const workflowRunsMainQuery = {
  ...workflowRunsMainQueryBase,
  type: QueryRouteQueryType.Deferred,
} as const
